import {
  formatDateTime,
  formatTime,
} from '@/features/AuditLogs/utils/auditLogUtils'
import { Box, Table, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

interface AuditLogRowShellProps {
  /** Colour token for the left status-indicator bar. */
  statusColor: string
  isSelected: boolean
  onClick: () => void
  /** The content cells that follow the status indicator. */
  children: ReactNode
}

/**
 * Selectable audit-log table row: the clickable/hover/selected `Table.Row` and
 * the leading status-indicator cell. The trailing content cells are supplied by
 * the caller, so the audit and license-history rows share this chrome rather
 * than each re-declaring it.
 */
export function AuditLogRowShell({
  statusColor,
  isSelected,
  onClick,
  children,
}: Readonly<AuditLogRowShellProps>) {
  return (
    <Table.Row
      onClick={onClick}
      cursor="pointer"
      bg={isSelected ? 'blue.100' : undefined}
      _hover={{ bg: isSelected ? 'blue.200' : 'gray.50' }}
      transition="background-color 0.15s"
      outline={isSelected ? '2px solid' : undefined}
      outlineColor={isSelected ? 'blue.400' : undefined}
      outlineOffset="-2px"
    >
      {/* Status indicator cell */}
      <Table.Cell width="4px" p={0}>
        <Box
          w="4px"
          h="100%"
          minH="40px"
          bg={statusColor}
          borderLeftRadius="sm"
        />
      </Table.Cell>
      {children}
    </Table.Row>
  )
}

interface LogTimestampCellProps {
  timestamp?: number
  /** Show the full date; when false (grouped under a date header) show time only. */
  showDate: boolean
}

/** Monospace timestamp cell shared by the audit and license-history rows. */
export function LogTimestampCell({
  timestamp,
  showDate,
}: Readonly<LogTimestampCellProps>) {
  const formatted = showDate ? formatDateTime(timestamp) : formatTime(timestamp)
  return (
    <Table.Cell>
      <Text fontSize="xs" fontFamily="mono" whiteSpace="nowrap">
        {formatted}
      </Text>
    </Table.Cell>
  )
}
